/**
 * mrNormalize.js — нормировка яркости МРТ по гистограмме серии.
 *
 * ПОВОД. В панели рендеринга список «Перенос» состоял из трёх пунктов, и все
 * три — КТ. Досыпать в него МРТ было нельзя, и вот почему: у КТ есть единицы
 * Хаунсфилда — вода 0, воздух −1000, кость +1000, и это ФИЗИЧЕСКАЯ шкала,
 * одинаковая на любом аппарате. Поэтому «кость 300…1500 HU» работает везде.
 *
 * У МРТ такой шкалы НЕТ. Яркость вокселя — произвольное число, зависящее от
 * аппарата, катушки, последовательности, усиления приёмника и даже от того, что
 * попало в кадр. Одна и та же белая материя на двух исследованиях легко даёт 300
 * и 3000. Один и тот же «перенос» на них даст разную картинку, а на третьем —
 * пустой экран. Поэтому передаточную функцию для МРТ нужно строить ОТ ДАННЫХ:
 * по гистограмме конкретной серии.
 *
 * ЧТО ЗДЕСЬ СЧИТАЕТСЯ.
 *
 *   1. Гистограмма по всем вокселям.
 *   2. Порог фона: воздух и шум за пределами тела дают высокий пик у нуля.
 *      Считать перцентили вместе с ним нельзя — «2-й перцентиль» окажется
 *      воздухом, и всё окно уедет. Порог ищется как первая впадина после
 *      первого пика.
 *   3. Устойчивое окно: перцентили по ТКАНИ (без фона), по умолчанию 2% и 98%.
 *      Не минимум и максимум: одна яркая точка (жир, артефакт, метка) растянула
 *      бы окно на весь диапазон.
 *   4. Нормировка: ткань в [0, 1]. После неё сравнение двух серий осмысленно,
 *      и один и тот же перенос даёт на них похожую картинку — это и проверяется
 *      тестом на «двух аппаратах» с разным усилением и смещением.
 *
 * ЧЕГО ЗДЕСЬ НЕТ, и это важно сказать вслух:
 *
 *   · Это НЕ количественная нормировка. Ни Nyúl, ни z-score по маске мозга, ни
 *     WhiteStripe: они требуют сегментации и обучающего набора. Здесь — рабочая
 *     нормировка ДЛЯ ПОКАЗА, чтобы объёмный рендер МРТ был воспроизводим.
 *     Ставить по нормированному числу диагноз нельзя, и в интерфейсе это сказано.
 *   · Нормировка не делает серии сопоставимыми количественно: T1-взвешенная и
 *     T2-взвешенная после неё всё равно про разное.
 *   · Если гистограмма вырожденная (пустой объём, одно значение, только фон) —
 *     возвращается `надёжно: false` с причиной, и вызывающий обязан отказаться
 *     от построения, а не показывать случайное окно. Тот же приём, что в
 *     жёстком совмещении и в CPR.
 */

const КОРЗИН_ПО_УМОЛЧАНИЮ = 256;

/**
 * Гистограмма массива.
 * @param {ArrayLike<number>} данные
 * @param {{корзин?: number, шаг?: number}} нстр шаг — прореживание для скорости
 * @returns {{корзины: Float64Array, минимум: number, максимум: number, ширинаКорзины: number, всего: number}}
 */
export function гистограмма(данные, нстр = {}) {
  const корзин = Math.max(8, нстр.корзин || КОРЗИН_ПО_УМОЛЧАНИЮ);
  const шаг = Math.max(1, Math.floor(нстр.шаг || 1));
  let мин = Infinity, макс = -Infinity, всего = 0;
  for (let i = 0; i < данные.length; i += шаг) {
    const v = данные[i];
    if (!Number.isFinite(v)) continue;
    if (v < мин) мин = v;
    if (v > макс) макс = v;
    всего++;
  }
  if (!всего || !Number.isFinite(мин) || !Number.isFinite(макс)) {
    return { корзины: new Float64Array(корзин), минимум: 0, максимум: 0, ширинаКорзины: 1, всего: 0 };
  }
  const ширина = (макс - мин) / корзин || 1;
  const корзины = new Float64Array(корзин);
  for (let i = 0; i < данные.length; i += шаг) {
    const v = данные[i];
    if (!Number.isFinite(v)) continue;
    let к = Math.floor((v - мин) / ширина);
    if (к < 0) к = 0; else if (к >= корзин) к = корзин - 1;
    корзины[к]++;
  }
  return { корзины, минимум: мин, максимум: макс, ширинаКорзины: ширина, всего };
}

/** Значение яркости по номеру корзины (центр корзины). */
export function значениеКорзины(гист, номер) {
  return гист.минимум + (номер + 0.5) * гист.ширинаКорзины;
}

/**
 * Порог фона: первая впадина после первого пика.
 *
 * Воздух и шум вокруг тела дают самый высокий пик у левого края. Ткань — пик
 * правее. Между ними впадина, и это и есть порог. Если впадины нет (одна мода,
 * например только фон или только однородная ткань) — возвращается 0 и признак
 * «фон не отделён», чтобы вызывающий знал, что перцентили считались по всему.
 *
 * @returns {{порог: number, номерКорзины: number, отделён: boolean}}
 */
export function порогФона(гист, нстр = {}) {
  const сглаживание = Math.max(1, нстр.сглаживание || 3);
  const к = гист.корзины;
  const n = к.length;
  if (!гист.всего) return { порог: 0, номерКорзины: 0, отделён: false };

  // Сглаживаем: на сырой гистограмме локальных минимумов десятки, и первый же
  // будет шумом, а не границей фона.
  const г = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0, c = 0;
    for (let j = i - сглаживание; j <= i + сглаживание; j++) {
      if (j < 0 || j >= n) continue;
      s += к[j]; c++;
    }
    г[i] = s / c;
  }

  // Первый пик — это фон (воздух и шум вокруг тела).
  let пик = 0;
  for (let i = 1; i < n; i++) if (г[i] > г[пик]) { пик = i; } else if (г[i] < г[пик] * 0.5) break;

  // Конец лепестка фона: идём вправо, пока держится хотя бы 5% высоты пика.
  //
  // ПОВОД. Пик воздуха бывает очень узким и очень высоким (в синтетике — 36 000
  // вокселей в одной корзине из 256). Сглаживание размазывает его на несколько
  // корзин, и если искать вторую моду сразу за пиком, самой высокой окажется
  // размазанный хвост САМОГО ЖЕ фона. Первая версия так и делала — и объявляла
  // двумодальную гистограмму одномодальной, то есть отказывалась строить окно
  // там, где всё в порядке. Поймано тестом «два аппарата».
  let конецФона = пик;
  while (конецФона < n - 1 && г[конецФона] > г[пик] * 0.05) конецФона++;

  // Вторая мода — ткань: самая высокая корзина за лепестком фона.
  let второй = -1;
  for (let i = конецФона + 1; i < n; i++) if (второй < 0 || г[i] > г[второй]) второй = i;
  if (второй < 0) return { порог: гист.минимум, номерКорзины: 0, отделён: false };

  // Впадина — минимум между модами.
  let впадина = конецФона;
  for (let i = конецФона; i < второй; i++) if (г[i] < г[впадина]) впадина = i;

  // Настоящая двумодальность требует двух условий, и оба нужны.
  //
  // ПОВОД для второго: первая версия проверяла только «есть локальный минимум
  // ниже половины пика» — и на объёме из ЧИСТОГО ШУМА объявляла фон отделённым,
  // потому что на спадающем хвосте колокола такой минимум находится всегда.
  // Тест это поймал: окно строилось по шуму и считалось надёжным.
  const впадинаНастоящая = г[впадина] < г[второй] * 0.5;   // между модами есть провал
  const втораяМодаЕсть = г[второй] > г[пик] * 0.005;       // и вторая мода не пыль
  if (!впадинаНастоящая || !втораяМодаЕсть) {
    return { порог: гист.минимум, номерКорзины: 0, отделён: false };
  }
  return { порог: значениеКорзины(гист, впадина), номерКорзины: впадина, отделён: true };
}

/**
 * Перцентиль по гистограмме, считая только корзины от `сНомера` и правее.
 * @param {number} доля 0…1
 */
export function перцентильПоГистограмме(гист, доля, сНомера = 0) {
  const к = гист.корзины;
  let сумма = 0;
  for (let i = сНомера; i < к.length; i++) сумма += к[i];
  if (сумма <= 0) return значениеКорзины(гист, сНомера);
  const цель = сумма * Math.max(0, Math.min(1, доля));
  let накоплено = 0;
  for (let i = сНомера; i < к.length; i++) {
    накоплено += к[i];
    if (накоплено >= цель) return значениеКорзины(гист, i);
  }
  return значениеКорзины(гист, к.length - 1);
}

/**
 * Окно яркости серии МРТ — то, что подаётся в передаточную функцию.
 *
 * @param {ArrayLike<number>} данные скаляры объёма
 * @param {{низ?: number, верх?: number, корзин?: number, шаг?: number}} нстр
 * @returns {{
 *   надёжно: boolean, причина: string|null,
 *   низ: number, верх: number, ww: number, wc: number,
 *   порогФона: number, фонОтделён: boolean, долиТкани: number,
 *   гистограмма: object
 * }}
 */
export function окноСерии(данные, нстр = {}) {
  const низДоля = нстр.низ ?? 0.02;
  const верхДоля = нстр.верх ?? 0.98;
  const пусто = {
    надёжно: false, низ: 0, верх: 0, ww: 0, wc: 0,
    порогФона: 0, фонОтделён: false, долиТкани: 0, гистограмма: null,
  };

  if (!данные || !данные.length) return { ...пусто, причина: 'нет данных объёма' };

  const гист = гистограмма(данные, нстр);
  if (!гист.всего) return { ...пусто, причина: 'в объёме нет пригодных чисел' };
  if (гист.максимум - гист.минимум < 1e-9) {
    return { ...пусто, гистограмма: гист, причина: 'все воксели одинаковые — окно не определено' };
  }

  const фон = порогФона(гист, нстр);

  // Одномодальная гистограмма — отказ, и вот почему это правильнее «как-нибудь».
  //
  // Если пика ткани нет, отличить ткань от шума нечем: на объёме, где в кадре
  // только воздух, перцентили 2% и 98% дадут аккуратное окно ПО ШУМУ, и рендер
  // получится картинкой из ничего. Проверка это и поймала: пустой объём с шумом
  // проходил как надёжный.
  //
  // Цена решения названа честно: серия, обрезанная по телу вплотную (воздуха в
  // кадре почти нет), тоже получит отказ. Тогда врач увидит причину и сможет
  // взять пресет КТ-типа или показать срезы — это лучше, чем объём, собранный
  // по случайному окну.
  if (!фон.отделён) {
    return {
      ...пусто, гистограмма: гист, порогФона: гист.минимум, фонОтделён: false,
      причина: 'гистограмма одномодальная: ткань от фона не отделяется (в кадре только шум или однородный объём)',
    };
  }

  const сНомера = фон.номерКорзины;

  let вокселейТкани = 0;
  for (let i = сНомера; i < гист.корзины.length; i++) вокселейТкани += гист.корзины[i];
  const доляТкани = вокселейТкани / гист.всего;

  const низ = перцентильПоГистограмме(гист, низДоля, сНомера);
  const верх = перцентильПоГистограмме(гист, верхДоля, сНомера);

  if (верх - низ < гист.ширинаКорзины) {
    return {
      ...пусто, гистограмма: гист, порогФона: фон.порог, фонОтделён: фон.отделён,
      долиТкани: доляТкани, причина: 'окно вырождено: 2-й и 98-й перцентили совпали',
    };
  }
  // Ткани почти нет — скорее всего пустой или битый объём. Молча отдать окно
  // «по шуму» нельзя: рендер получится случайным.
  if (доляТкани < 0.005) {
    return {
      ...пусто, гистограмма: гист, порогФона: фон.порог, фонОтделён: фон.отделён,
      долиТкани: доляТкани, причина: `ткани в объёме меньше 0,5% (${(доляТкани * 100).toFixed(2)}%)`,
    };
  }

  return {
    надёжно: true, причина: null,
    низ, верх,
    ww: верх - низ,
    wc: (верх + низ) / 2,
    порогФона: фон.порог, фонОтделён: фон.отделён,
    долиТкани: доляТкани,
    гистограмма: гист,
  };
}

/** Нормировать значение по окну серии: ткань → [0, 1], за краями зажимается. */
export function нормировать(значение, окно) {
  if (!окно || !(окно.верх > окно.низ)) return 0;
  const t = (значение - окно.низ) / (окно.верх - окно.низ);
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** Обратно: доля 0…1 → яркость в единицах этой серии. Нужно для окон и TF. */
export function изНормированного(доля, окно) {
  if (!окно || !(окно.верх > окно.низ)) return 0;
  return окно.низ + Math.max(0, Math.min(1, доля)) * (окно.верх - окно.низ);
}

/**
 * Точки передаточной функции для МРТ, заданные в НОРМИРОВАННЫХ долях, —
 * переводятся в яркость этой серии.
 *
 * Пресеты нарочно простые и описаны словами: «мозг» показывает паренхиму и
 * гасит фон, «сосуды/TOF» оставляет только самое яркое (в TOF-ангиографии
 * кровь — самый яркий сигнал), «обзор» — почти линейный.
 */
export const МР_ПРЕСЕТЫ = {
  'mr-brain': {
    имя: 'МР мозг',
    точки: [[0.00, 0.00], [0.25, 0.05], [0.50, 0.35], [0.75, 0.75], [1.00, 0.95]],
  },
  'mr-vessels': {
    имя: 'МР сосуды (TOF)',
    точки: [[0.00, 0.00], [0.55, 0.00], [0.75, 0.35], [0.90, 0.85], [1.00, 1.00]],
  },
  'mr-overview': {
    имя: 'МР обзор',
    точки: [[0.00, 0.00], [0.10, 0.05], [0.50, 0.45], [0.90, 0.85], [1.00, 1.00]],
  },
};

/**
 * Собрать передаточную функцию для конкретной серии: доли → яркость серии.
 * @returns {{ид: string, имя: string, точки: Array<[number, number]>, окно: object}|null}
 */
export function переносДляСерии(идПресета, окно) {
  const п = МР_ПРЕСЕТЫ[идПресета];
  if (!п || !окно?.надёжно) return null;
  return {
    ид: идПресета,
    имя: п.имя,
    точки: п.точки.map(([доля, непрозрачность]) => [изНормированного(доля, окно), непрозрачность]),
    окно,
  };
}

export default {
  гистограмма, значениеКорзины, порогФона, перцентильПоГистограмме,
  окноСерии, нормировать, изНормированного, МР_ПРЕСЕТЫ, переносДляСерии,
};
